import { Control, DomUtil, Map, LatLngBounds } from 'leaflet';
import { EventEmitter } from '@angular/core';

export class BoxSelectionControl extends Control {
  private container!: HTMLDivElement;
  private isSelecting = false;
  private startPoint: { x: number; y: number } | null = null;
  private selectionBox: HTMLDivElement | null = null;
  private allControls: HTMLElement[] = [];
  private originalTapTolerance = 15;
  private map: Map | null = null;

  // Bound Event-Handler für DOM-Events (damit wir sie später entfernen können)
  private boundTouchStart: ((e: TouchEvent) => void) | null = null;
  private boundTouchMove: ((e: TouchEvent) => void) | null = null;
  private boundTouchEnd: ((e: TouchEvent) => void) | null = null;

  // Mindestgröße in Pixeln, um leere Rechtecke (Einzelklicks) abzulehnen
  private readonly MIN_SELECTION_SIZE = 10;

  // EventEmitter für Angular
  public boundsSelected = new EventEmitter<LatLngBounds>();
  public selectionCancelled = new EventEmitter<void>();

  constructor(options?: any) {
    super(options);
  }

  override onAdd(map: Map): HTMLElement {
    this.map = map;
    // Leerer Container - Cancel-Button wurde entfernt, da Abbrechen über
    // Tool-Wechsel oder erneuten Klick auf Lupe erfolgt
    this.container = DomUtil.create('div', 'leaflet-bar leaflet-control');
    this.container.style.display = 'none';

    return this.container;
  }

  override onRemove(): void {
    this.map = null;
  }

  public startSelection(): void {
    if (!this.map || this.isSelecting) return;

    this.isSelecting = true;

    // Alle Controls ausblenden (außer diesem)
    this.hideAllControls();

    // Cursor ändern
    this.map.getContainer().style.cursor = 'crosshair';

    // Touch-Interaktion konfigurieren: Nur Zwei-Finger-Gesten erlauben
    if (this.map.dragging) {
      this.map.dragging.disable();
    }

    // Tap-Handler deaktivieren, um versehentliche Taps zu vermeiden
    if ((this.map as any).tap) {
      this.originalTapTolerance = (this.map as any).tap.options.tapTolerance;
      (this.map as any).tap.disable();
    }

    // Mouse Events über Leaflet
    this.map.on('mousedown', this.onMouseDown, this);

    // Touch Events direkt auf dem DOM mit { passive: false } um preventDefault zu ermöglichen
    this.boundTouchStart = this.onDomTouchStart.bind(this);
    this.map.getContainer().addEventListener('touchstart', this.boundTouchStart!, { passive: false });
  }

  public cancelSelection(): void {
    if (!this.map) return;

    this.isSelecting = false;

    // Selection Box entfernen falls vorhanden
    if (this.selectionBox && this.selectionBox.parentNode) {
      this.selectionBox.parentNode.removeChild(this.selectionBox);
      this.selectionBox = null;
    }

    // Mouse Events entfernen (Leaflet)
    this.map.off('mousedown', this.onMouseDown, this);
    this.map.off('mousemove', this.onMouseMove, this);
    this.map.off('mouseup', this.onMouseUp, this);

    // Touch Events entfernen (DOM)
    const container = this.map.getContainer();
    if (this.boundTouchStart) {
      container.removeEventListener('touchstart', this.boundTouchStart);
      this.boundTouchStart = null;
    }
    if (this.boundTouchMove) {
      container.removeEventListener('touchmove', this.boundTouchMove);
      this.boundTouchMove = null;
    }
    if (this.boundTouchEnd) {
      container.removeEventListener('touchend', this.boundTouchEnd);
      this.boundTouchEnd = null;
    }

    // Cursor zurücksetzen
    this.map.getContainer().style.cursor = '';

    // Touch-Interaktion wiederherstellen
    if (this.map.dragging) {
      this.map.dragging.enable();
    }

    if ((this.map as any).tap) {
      (this.map as any).tap.enable();
    }

    // Controls wieder einblenden
    this.showAllControls();
  }

  private onMouseDown(e: any): void {
    if (!this.map) return;

    this.startPoint = {
      x: e.containerPoint.x,
      y: e.containerPoint.y
    };

    // Selection Box erstellen
    this.selectionBox = DomUtil.create('div', 'leaflet-selection-box');
    this.selectionBox.style.position = 'absolute';
    this.selectionBox.style.border = '2px dashed #3388ff';
    this.selectionBox.style.backgroundColor = 'rgba(51, 136, 255, 0.1)';
    this.selectionBox.style.left = this.startPoint.x + 'px';
    this.selectionBox.style.top = this.startPoint.y + 'px';
    this.selectionBox.style.width = '0px';
    this.selectionBox.style.height = '0px';
    this.selectionBox.style.zIndex = '1000';

    this.map.getContainer().appendChild(this.selectionBox);

    // Mouse Move & Up Events registrieren
    this.map.on('mousemove', this.onMouseMove, this);
    this.map.on('mouseup', this.onMouseUp, this);

    // Verhindere Map-Dragging während der Selektion
    this.map.dragging.disable();
  }

  private onDomTouchStart(e: TouchEvent): void {
    if (!this.map) return;

    // Nur Ein-Finger-Touch für Selektion erlauben
    if (e.touches.length !== 1) {
      return;
    }

    // Verhindere Standard-Touch-Verhalten SOFORT um Karten-Panning zu verhindern
    e.preventDefault();
    e.stopPropagation();

    const touch = e.touches[0];
    const rect = this.map.getContainer().getBoundingClientRect();

    this.startPoint = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };

    // Selection Box erstellen
    this.selectionBox = DomUtil.create('div', 'leaflet-selection-box');
    this.selectionBox.style.position = 'absolute';
    this.selectionBox.style.border = '2px dashed #3388ff';
    this.selectionBox.style.backgroundColor = 'rgba(51, 136, 255, 0.1)';
    this.selectionBox.style.left = this.startPoint.x + 'px';
    this.selectionBox.style.top = this.startPoint.y + 'px';
    this.selectionBox.style.width = '0px';
    this.selectionBox.style.height = '0px';
    this.selectionBox.style.zIndex = '1000';
    this.selectionBox.style.pointerEvents = 'none';

    this.map.getContainer().appendChild(this.selectionBox);

    // Touch Move & End Events registrieren (DOM-Events mit passive: false)
    const container = this.map.getContainer();
    this.boundTouchMove = this.onDomTouchMove.bind(this);
    this.boundTouchEnd = this.onDomTouchEnd.bind(this);
    container.addEventListener('touchmove', this.boundTouchMove!, { passive: false });
    container.addEventListener('touchend', this.boundTouchEnd!, { passive: false });
  }

  private onMouseMove(e: any): void {
    if (!this.startPoint || !this.selectionBox || !this.map) return;

    const currentPoint = {
      x: e.containerPoint.x,
      y: e.containerPoint.y
    };

    const minX = Math.min(this.startPoint.x, currentPoint.x);
    const maxX = Math.max(this.startPoint.x, currentPoint.x);
    const minY = Math.min(this.startPoint.y, currentPoint.y);
    const maxY = Math.max(this.startPoint.y, currentPoint.y);

    this.selectionBox.style.left = minX + 'px';
    this.selectionBox.style.top = minY + 'px';
    this.selectionBox.style.width = (maxX - minX) + 'px';
    this.selectionBox.style.height = (maxY - minY) + 'px';
  }

  private onDomTouchMove(e: TouchEvent): void {
    if (!this.startPoint || !this.selectionBox || !this.map) return;

    // Nur Ein-Finger-Touch verarbeiten
    if (e.touches.length !== 1) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    const touch = e.touches[0];
    const rect = this.map.getContainer().getBoundingClientRect();

    const currentPoint = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };

    const minX = Math.min(this.startPoint.x, currentPoint.x);
    const maxX = Math.max(this.startPoint.x, currentPoint.x);
    const minY = Math.min(this.startPoint.y, currentPoint.y);
    const maxY = Math.max(this.startPoint.y, currentPoint.y);

    this.selectionBox.style.left = minX + 'px';
    this.selectionBox.style.top = minY + 'px';
    this.selectionBox.style.width = (maxX - minX) + 'px';
    this.selectionBox.style.height = (maxY - minY) + 'px';
  }

  private onMouseUp(e: any): void {
    if (!this.startPoint || !this.map) return;

    const endPoint = {
      x: e.containerPoint.x,
      y: e.containerPoint.y
    };

    // Mindestgröße prüfen - zu kleine Rechtecke (Einzelklicks) ablehnen
    const width = Math.abs(endPoint.x - this.startPoint.x);
    const height = Math.abs(endPoint.y - this.startPoint.y);
    if (width < this.MIN_SELECTION_SIZE || height < this.MIN_SELECTION_SIZE) {
      // Zu klein - abbrechen und cancelled Event emittieren
      this.cancelSelection();
      this.map.dragging.enable();
      this.selectionCancelled.emit();
      return;
    }

    // LatLngBounds berechnen
    const southWest = this.map.containerPointToLatLng([
      Math.min(this.startPoint.x, endPoint.x),
      Math.max(this.startPoint.y, endPoint.y)
    ]);
    const northEast = this.map.containerPointToLatLng([
      Math.max(this.startPoint.x, endPoint.x),
      Math.min(this.startPoint.y, endPoint.y)
    ]);

    const bounds = new LatLngBounds(southWest, northEast);

    // Emit das Ergebnis
    this.boundsSelected.emit(bounds);

    // Cleanup
    this.cancelSelection();

    // Map-Dragging wieder aktivieren
    this.map.dragging.enable();
  }

  private onDomTouchEnd(e: TouchEvent): void {
    if (!this.startPoint || !this.map) return;

    e.preventDefault();
    e.stopPropagation();

    const touch = e.changedTouches[0];
    const rect = this.map.getContainer().getBoundingClientRect();

    const endPoint = {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top
    };

    // Mindestgröße prüfen - zu kleine Rechtecke (Einzelklicks) ablehnen
    const width = Math.abs(endPoint.x - this.startPoint.x);
    const height = Math.abs(endPoint.y - this.startPoint.y);
    if (width < this.MIN_SELECTION_SIZE || height < this.MIN_SELECTION_SIZE) {
      // Zu klein - abbrechen und cancelled Event emittieren
      this.cancelSelection();
      this.selectionCancelled.emit();
      return;
    }

    // LatLngBounds berechnen
    const southWest = this.map.containerPointToLatLng([
      Math.min(this.startPoint.x, endPoint.x),
      Math.max(this.startPoint.y, endPoint.y)
    ]);
    const northEast = this.map.containerPointToLatLng([
      Math.max(this.startPoint.x, endPoint.x),
      Math.min(this.startPoint.y, endPoint.y)
    ]);

    const bounds = new LatLngBounds(southWest, northEast);

    // Emit das Ergebnis
    this.boundsSelected.emit(bounds);

    // Cleanup
    this.cancelSelection();
  }

  private hideAllControls(): void {
    if (!this.map) return;

    const mapContainer = this.map.getContainer();
    const controls = mapContainer.querySelectorAll('.leaflet-control');

    this.allControls = [];

    controls.forEach((control) => {
      const htmlControl = control as HTMLElement;
      // Nicht dieses Control verstecken
      if (htmlControl !== this.container && htmlControl.style.display !== 'none') {
        this.allControls.push(htmlControl);
        htmlControl.style.display = 'none';
      }
    });
  }

  private showAllControls(): void {
    this.allControls.forEach((control) => {
      control.style.display = '';
    });
    this.allControls = [];
  }
}
